/**
 * insert-variable-button.tsx — Nút "Chèn biến / công cụ": chèn token {{var.…}} / {{tool.…}} vào ô soạn
 * tại vị trí con trỏ. Danh mục lấy từ GET /ai/bots/:id/variable-catalog (backend thay token khi dựng prompt).
 */
import { useMemo, useState } from 'react'
import { Braces, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useVariableCatalog, withLiveAttributes } from './train-api'
import type { CollectAttribute } from './bot-settings'

type Item = { token: string; label: string; hint?: string }

export function InsertVariableButton({ botId, onInsert, attributes, disabled, size = 'sm' }: {
  botId: string
  onInsert: (token: string) => void
  attributes?: CollectAttribute[]
  disabled?: boolean
  size?: 'sm' | 'default'
}) {
  const { t } = useTranslation('aiTrainUi')
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const { data, isLoading } = useVariableCatalog(open ? botId : undefined)
  const groups = useMemo(() => {
    const cat = withLiveAttributes(data, attributes ?? [])
    if (!cat) return []
    const vars = cat.groups.map((g) => ({ id: g.id, label: g.label, items: g.variables.map<Item>((v) => ({ token: v.variable, label: v.name })) }))
    const toolGroups = new Map<string, Item[]>()
    for (const tool of cat.tools) {
      const list = toolGroups.get(tool.group) ?? []
      list.push({ token: tool.token, label: tool.name, hint: tool.description })
      toolGroups.set(tool.group, list)
    }
    return [...vars, ...[...toolGroups].map(([label, items]) => ({ id: `tool-${label}`, label: t('insertVariable.toolGroup', { group: label }), items }))]
  }, [data, attributes, t])
  const needle = q.trim().toLowerCase()
  const shown = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => !needle || i.label.toLowerCase().includes(needle) || i.token.toLowerCase().includes(needle)) }))
    .filter((g) => g.items.length)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size={size} disabled={disabled} title={t('insertVariable.title')}>
          <Braces className="h-3.5 w-3.5" /> {t('insertVariable.button')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <div className="relative mb-2">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('insertVariable.searchPlaceholder')} className="h-8 pl-7 text-xs" />
        </div>
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {isLoading && <p className="p-2 text-center text-xs text-muted-foreground">{t('insertVariable.loading')}</p>}
          {shown.map((g) => (
            <div key={g.id}>
              <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</p>
              {g.items.map((it) => (
                <button
                  key={it.token}
                  type="button"
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                  onClick={() => { onInsert(it.token); setOpen(false) }}
                  title={it.hint}
                >
                  <span className="min-w-0 flex-1 truncate">{it.label}</span>
                  <code className="max-w-[45%] shrink-0 truncate rounded bg-muted px-1 text-[10px] text-muted-foreground">{it.token}</code>
                </button>
              ))}
            </div>
          ))}
          {!isLoading && shown.length === 0 && <p className="p-2 text-center text-xs text-muted-foreground">{t('insertVariable.noMatch')}</p>}
        </div>
        <p className="mt-2 border-t px-1 pt-2 text-[11px] text-muted-foreground">
          {t('insertVariable.note')}
        </p>
      </PopoverContent>
    </Popover>
  )
}
